import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { TextInput } from "../../../../form/textInput";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { AuthenticateSelect } from "../../../../auth/select";

type InstagramPostingProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type InstagramPostingRequest = "image_post" | "video_post" | "carousel_post";

function normalizeInstagramRequest(request?: string): InstagramPostingRequest {
  if (
    request === "image_post" ||
    request === "video_post" ||
    request === "carousel_post"
  ) {
    return request;
  }
  return "image_post";
}

export function InstagramPosting({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: InstagramPostingProps) {
  const [requests, setRequests] = React.useState<selectDataType[]>([
    { id: 1, name: "Image post", refId: "image_post", isActive: true },
    { id: 2, name: "Video post", refId: "video_post" },
    { id: 3, name: "Carousel post", refId: "carousel_post" },
  ]);
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(() => {
    if (setChildMinimize) {
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    }
    if (setChildSettings) {
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    }
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    const request = normalizeInstagramRequest(data.value?.request);
    setRequests((prev) =>
      prev.map((item) => ({ ...item, isActive: item.refId === request }))
    );
    if (!data.value || data.value.request !== request) {
      updateNodeValue(id, setNodes, { request });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.request, id, setNodes]);

  const handleSelectRequest = (selectId: number) => {
    const refId = requests.find((item) => item.id === selectId)?.refId as
      | InstagramPostingRequest
      | undefined;
    if (!refId) return;

    setRequests((prev) =>
      prev.map((item) => ({
        ...item,
        isActive: item.id === selectId,
      }))
    );
    updateNodeValue(id, setNodes, { request: refId });
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    keyRef?: string
  ) => {
    if (!keyRef) return;
    updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
  };

  const request = normalizeInstagramRequest(data.value?.request);
  const hasConnection = !!(
    data.value?.connection &&
    (data.value?.connection?._id ||
      data.value?.connection?.id ||
      data.value?.connectionId)
  );
  const hasRequestFields =
    request === "image_post"
      ? !!data.value?.image_url
      : request === "video_post"
      ? !!data.value?.video_url
      : !!data.value?.media_urls;
  const isWarning = !hasConnection || !hasRequestFields;
   console.log("InstagramPosting render", { connection: data });

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "420px" }}
          >
            <Styled style={{ marginTop: "-13px" }}>
              <AuthenticateSelect
              type="instagramPosting"
              defaultId={
                data.value?.connection?._id ||
                data.value?.connection?.id ||
                data.value?.connectionId
              }
              handleAuthenticate={(type, connection) => {
                if (type === "add") {
                const connectionId = connection._id || connection.id;
                updateNodeValue(id, setNodes, {
                  connection,
                  connectionId,
                  instagram_business_account_id:
                  data.value?.instagram_business_account_id ||
                  connection.instagram_business_account_id,
                });
                } else {
                updateNodeValue(id, setNodes, {
                  connection: null,
                  connectionId: null,
                });
                }
              }}
              withLine={!data.value?.connection}
              background={data.background}
              />

              <TextInput
              keyRef="instagram_business_account_id"
              value={
                data.value?.instagram_business_account_id ||
                data.value?.connection?.instagram_business_account_id ||
                ""
              }
              title="Account ID"
              placeholder="instagram_business_account_id"
              handleChange={handleChangeInput}
              />

              <Select
              title="Type"
              data={requests}
              handleSelectItem={handleSelectRequest}
              isSmall={true}
              />

              {/* Filter fields for posting */}
              {request === "image_post" ? (
              <>
                <TextInput
                keyRef="image_url"
                value={data.value?.image_url}
                title="Image URL"
                placeholder="https://example.com/image.png"
                handleChange={handleChangeInput}
                />
                <Textarea
                keyRef="caption"
                value={data.value?.caption}
                placeholder="Write caption..."
                handleChange={handleChangeInput}
                />
                {/* Filter: Only post if image_url is valid */}
                {/* {!data.value?.image_url && (
                <div style={{ color: "red", marginTop: "8px" }}>
                  Please provide a valid image URL to post.
                </div>
                )} */}
              </>
              ) : request === "video_post" ? (
              <>
                <TextInput
                keyRef="video_url"
                value={data.value?.video_url}
                title="Video URL"
                placeholder="https://example.com/video.mp4"
                handleChange={handleChangeInput}
                />
                <Textarea
                keyRef="caption"
                value={data.value?.caption}
                placeholder="Write caption..."
                handleChange={handleChangeInput}
                />
                {/* Filter: Only post if video_url is valid */}
                {/* {!data.value?.video_url && (
                <div style={{ color: "red", marginTop: "8px" }}>
                  Please provide a valid video URL to post.
                </div>
                )} */}
              </>
              ) : (
              <>
                <Textarea
                keyRef="media_urls"
                value={data.value?.media_urls}
                placeholder={`One URL per line\nhttps://example.com/a.jpg\nhttps://example.com/b.mp4`}
                handleChange={handleChangeInput}
                />
                <Textarea
                keyRef="caption"
                value={data.value?.caption}
                placeholder="Write caption..."
                handleChange={handleChangeInput}
                />
                {/* Filter: Only post if media_urls is not empty */}
                {/* {!data.value?.media_urls && (
                <div style={{ color: "red", marginTop: "8px" }}>
                  Please provide at least one media URL to post.
                </div>
                )} */}
              </>
              )}
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}